import { gsap } from "gsap";
import { useLayoutEffect, useRef } from "react";

import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";

import { MOTION_DURATION, MOTION_EASE } from "./motion.tokens";

export interface ColocaCountUpProps {
  /** The number to land on. */
  value: number;
  /** Formats every frame and the final one, so the end state is byte for byte what the formatter says. */
  format: (value: number) => string;
  className?: string;
}

/**
 * A figure that counts up from zero once, on mount. The element renders the real formatted value first, so a
 * reader with reduced motion, or a failed script, sees the right number and never a 0.
 */
export function ColocaCountUp({ value, format, className }: ColocaCountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduced = usePrefersReducedMotion();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || reduced || !Number.isFinite(value) || value <= 0) return;
    const state = { n: 0 };
    const tween = gsap.to(state, {
      n: value,
      duration: MOTION_DURATION.count,
      ease: MOTION_EASE.enter,
      onUpdate: () => {
        el.textContent = format(state.n);
      },
      onComplete: () => {
        el.textContent = format(value);
      },
    });
    return () => {
      tween.kill();
      el.textContent = format(value);
    };
  }, [value, format, reduced]);

  return (
    <span ref={ref} className={className}>
      {format(value)}
    </span>
  );
}
